import { BasicOption, Country, HierarchicalOption, Language, Option } from '@/models/Global'
import { ProductInput } from '@/models/Import'
import { ProductTypes, SimplifiedProduct } from '@/models/Product'
import {
  AdvancedProperty,
  ComputedBaseProperty,
  EditableAdvancedProperty,
  InstructionType,
  PropertyDeadlineLevel,
  PropertyDeadlines,
  PropertyMapping,
  PropertyMappingType,
  StoredProperty,
} from '@/models/Property'
import { ProductAvailabilityOption } from '@/models/Settings'
import { RegisteredTaxonomy } from '@/models/Taxonomy'
import { TranslationNamespace } from '@/models/Translation'
import { useDefaultStore } from '@/store'
import { getPropertyKey, getTaxonomyFromProperty } from '@/utils/propertyUtils'
import { areComparable, sortHierarchically, toComparable, toSafeString } from '@/utils/tools'
import { ComputeInstructions } from '@burde/pim-shared'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useTranslation } from '../composables/useTranslation'
import { useTaxonomiesStore } from './taxonomiesStore'

const unknownKey = 'unknown'

export const usePropertyStore = defineStore('property-store', () => {
  const store = useDefaultStore()
  const taxonomiesStore = useTaxonomiesStore()
  const { translate } = useTranslation()

  // ------------------------
  // -- Reactive variables --
  // ------------------------
  const storedProperties = ref<Array<Array<StoredProperty>>>([])
  const deadlines = ref<PropertyDeadlines>()

  const propertyResetKeys = ref<{ [sku: string]: { row: number; properties: { [comparable: string]: number } } }>({})
  // const propertyResetKeys: { [sku: string]: Ref<{ row: number; properties: { [comparable: string]: number } }> } = {}

  const stagedPropertyOptionsByComparable = ref<{ [propertyKey: string]: HierarchicalOption<string | number>[] }>({})

  const showColouredDeadlineHeaders = ref(false)

  const propertyMappings = ref<Partial<Record<PropertyMappingType, PropertyMapping[]>>>()

  const propertyInstructions = ref<Record<InstructionType, Partial<Record<string, ComputeInstructions[]>>>>()

  // --------------
  // -- Computed --
  // --------------

  const storedPropertiesFlat = computed(() => storedProperties.value.flat())

  const storedPropertiesByKey = computed(() =>
    storedPropertiesFlat.value.reduce<Partial<Record<string, StoredProperty>>>((acc, property) => {
      acc[getPropertyKey(property)] = property
      return acc
    }, {}),
  )

  const availableCountriesOptions = computed((): Option<Country>[] =>
    store.availableCountries
      .map((country) => ({
        value: country,
        displayValue: translate(country, 'countries', 'capitalize'),
        exclusiveToCountries: [country],
      }))
      .sort((a, b) => a.displayValue.localeCompare(b.displayValue)),
  )

  const availableLanguagesOptions = computed((): Option<Language>[] =>
    store.availableLanguages
      .map((language) => ({
        value: language,
        displayValue: translate(language, 'languages', 'capitalize'),
        exclusiveToLanguages: [language],
      }))
      .sort((a, b) => a.displayValue.localeCompare(b.displayValue)),
  )

  const productAvailabilityStatusOptions = computed(
    (): BasicOption<number>[] =>
      store.settings?.properties?.productAvailabilityOptions?.map((option) => ({
        displayValue: translate(option.label, 'terms', 'capitalize'),
        value: option.code,
      })) || [],
  )

  const productAvailabilityByCode = computed(() =>
    store.settings?.properties.productAvailabilityOptions?.reduce<Record<number, ProductAvailabilityOption>>((acc, option) => {
      acc[option.code] = {
        ...option,
        label: translate(option.label, 'terms', 'capitalize'),
      }
      return acc
    }, {}),
  )

  const availableProductTypes = computed(() => ProductTypes.map((type) => ({ value: type, displayValue: translate(type, 'terms', 'capitalize') })))

  const deadlinesByKey = computed(() =>
    deadlines.value?.levels.reduce<Partial<Record<string, PropertyDeadlineLevel>>>((acc, level) => {
      level.properties.forEach((property) => {
        acc[getPropertyKey(property)] = level
      })
      return acc
    }, {}),
  )

  const deadlinesById = computed(() =>
    deadlines.value?.levels.reduce<Partial<Record<string, PropertyDeadlineLevel>>>((acc, level) => {
      level.properties.forEach((property) => {
        if (property.id) {
          acc[property.id] = level
        }
      })
      return acc
    }, {}),
  )

  const derivedProperties = computed(() =>
    storedPropertiesFlat.value.reduce((map, prop) => {
      if ('dependencies' in prop) {
        prop.dependencies?.forEach((propertyKey) => {
          const arr = map.get(propertyKey) || []
          arr.push(getPropertyKey(prop))
          map.set(propertyKey, arr)
        })
      }

      return map
    }, new Map<string, string[]>()),
  )

  // -------------
  // -- Methods --
  // -------------
  // const incPropertyResetKey = (sku: string | undefined, property?: AdvancedProperty) => {
  const incPropertyResetKey = (sku: string | undefined, propertyKey?: string) => {
    const productKey = sku || unknownKey

    if (!propertyResetKeys.value[productKey]) {
      propertyResetKeys.value[productKey] = { row: 0, properties: {} }
    }
    // if (!propertyResetKeys[key].value) {
    //   propertyResetKeys[key] = ref({ row: 0, properties: {} })
    // }

    if (propertyKey) {
      // propertyResetKeys[key].value.properties[propertyKey] = (propertyResetKeys[key].value.properties[propertyKey] || 0) + 1
      propertyResetKeys.value[productKey].properties[propertyKey] = (propertyResetKeys.value[productKey].properties[propertyKey] || 0) + 1
    } else {
      // propertyResetKeys[key].value.row++
      propertyResetKeys.value[productKey].row++
    }
  }

  const getProductPropertyKey = (product: SimplifiedProduct, productInput: ProductInput | undefined, property: AdvancedProperty | undefined) => {
    if (!property || (!product && !productInput)) {
      return ''
    }

    const resetKey = propertyResetKeys.value[product.sku || unknownKey]
    // return `rowreset-${resetKey?.row ?? 0}_keyreset-${resetKey?.properties[property.key] ?? 0}_key-${property.key}_country-${property.culture?.country || ''}_language-${property.culture?.language || ''}_update-${product.updateTime}`
    return `rowreset-${resetKey?.row ?? 0}_keyreset-${resetKey?.properties[property.key] ?? 0}_key-${property.key}_culture-${property.culturePrefix || ''}_update-${product.updateTime}`
  }

  const getProfilesThatPropertyBelongsTo = (property: AdvancedProperty) =>
    store.templates.properties.public?.reduce((arr: string[], template) => {
      if (template.mappings.some((mapping) => areComparable(mapping.defaultName, property.defaultName))) {
        arr.push(template.name)
      }
      return arr
    }, [])

  /**
   * Use for computed props
   * For direct access where you need taxonomies store to be populate first, use getPropertyOptionsAsync
   */
  const getPropertyOptions = (property: AdvancedProperty) => {
    let options: HierarchicalOption<string | number>[] = []

    const taxonomy = getTaxonomyFromProperty(property)

    if (taxonomy === 'attributes') {
      if (property.id && property.hasTerms) {
        const attribute = taxonomiesStore.taxonomiesById.attributes.get(property.id)

        options =
          attribute?.terms?.map((term) => ({
            value: term.defaultName,
            displayValue: toSafeString(attribute.doNotTranslateChildren ? term.defaultName : term.name),
          })) || []
      }
    } else if (taxonomy === 'categories') {
      const objects = sortHierarchically(taxonomiesStore.taxonomies[taxonomy])

      options =
        objects.map((obj) => ({
          value: obj.defaultName,
          displayValue: obj.name,
          parentId: obj.parentId,
          level: obj.level,
        })) || []
    } else if (property.type === 'simpleTaxonomy' && taxonomy) {
      options =
        taxonomiesStore.taxonomies[taxonomy].map((obj) => ({
          value: obj.defaultName,
          displayValue: obj.name,
          exclusiveToCountries: obj.exclusiveToCountries,
        })) || []
    } else if (property.computedOptions === 'getAvailableCountries') {
      options = availableCountriesOptions.value
    } else if (property.computedOptions === 'getAvailableLanguages') {
      options = availableLanguagesOptions.value
    } else if (property.computedOptions === 'getProductAvailabilityStatuses') {
      options = productAvailabilityStatusOptions.value
    } else if (property.computedOptions === 'getAvailableProductTypes') {
      options = availableProductTypes.value
    }

    const stagedOptions = getStagedPropertyOptions(property)
    if (stagedOptions) {
      options = options.concat(stagedOptions)
    }
    return options
  }

  const getPropertyOptionsAsync = async (property: AdvancedProperty) => {
    const taxonomy = getTaxonomyFromProperty(property)
    if (taxonomy) {
      await taxonomiesStore.getTaxonomicData(taxonomy)
    }
    return getPropertyOptions(property)
  }

  /**
   * @returns true if created, false if not
   */
  const addStagedOption = (property: EditableAdvancedProperty, option: Omit<HierarchicalOption<string | number>, 'displayValue'>) => {
    const propertyKey = toComparable(property.defaultHeader)
    if (!stagedPropertyOptionsByComparable.value[propertyKey]) {
      stagedPropertyOptionsByComparable.value[propertyKey] = []
    }

    if (
      !stagedPropertyOptionsByComparable.value[propertyKey].some(
        (existingOption) => areComparable(existingOption.value, option.value) && option.parentId === existingOption.parentId,
      )
    ) {
      stagedPropertyOptionsByComparable.value[propertyKey].push({
        ...option,
        displayValue: option.value.toString(),
        staged: true,
      })

      return true
    }

    return false
  }

  const hideStagedOption = (property: EditableAdvancedProperty, option: HierarchicalOption<string | number>) => {
    const propertyKey = toComparable(property.defaultHeader)
    const foundIndex = stagedPropertyOptionsByComparable.value[propertyKey]?.findIndex(
      (otherOption) => otherOption.staged && otherOption.value === option.value && otherOption.parentId == option.parentId,
    )
    if (foundIndex > -1) {
      stagedPropertyOptionsByComparable.value[propertyKey].splice(foundIndex, 1)
    }
  }

  const getStagedPropertyOptions = (partialProperty: { defaultHeader: string; translationNamespace?: TranslationNamespace }) => {
    const propertyKey = toComparable(partialProperty.defaultHeader)
    return (
      stagedPropertyOptionsByComparable.value[propertyKey]?.map((option) => ({
        ...option,
        displayValue: partialProperty.translationNamespace
          ? translate(option.value, partialProperty.translationNamespace)
          : option.value.toString() || 'unknown',
      })) || []
    )
  }

  const setStagedPropertyOptions = (options: { [propertyKey: string]: HierarchicalOption<string | number>[] }) => {
    stagedPropertyOptionsByComparable.value = options
  }

  const resetStagedPropertyOptions = () => {
    stagedPropertyOptionsByComparable.value = {}
  }

  const getPropertyCulture = (property: AdvancedProperty) =>
    property.type === 'countrySpecific'
      ? store.availableCulturesByCountry.get(property.culturePrefix as Country)
      : property.type === 'languageSpecific'
        ? store.availableCulturesByLanguage.get(property.culturePrefix as Language)
        : undefined

  const getPropertyComputeInstructions = (property: ComputedBaseProperty) => {
    if (property.computeInstructions) {
      const func = propertyInstructions.value?.compute?.[property.computeInstructions]
      if (func) {
        return func
      }

      console.log(`Found no compute instructions by the name ${property.computeInstructions}`)
    }
  }

  const getPropertyExportFormattingInstructions = (property: StoredProperty | AdvancedProperty) => {
    if (property.formattingExportInstructions) {
      const func = propertyInstructions.value?.export?.[property.formattingExportInstructions]
      if (func) {
        return func
      }

      console.log(`Found no export instructions by the name ${property.formattingExportInstructions}`)
    }
  }

  const getPropertyByKey = <T extends StoredProperty>(key: string) => storedPropertiesByKey.value[key] as T | undefined

  return {
    propertyResetKeys,

    storedProperties,
    deadlines,
    deadlinesByKey,
    deadlinesById,
    showColouredDeadlineHeaders,
    propertyMappings,
    propertyInstructions,

    productAvailabilityByCode,

    availableCountriesOptions,
    availableLanguagesOptions,
    productAvailabilityStatusOptions,

    stagedPropertyOptionsByComparable,
    derivedProperties,

    getProductPropertyKey,
    incPropertyResetKey,
    getProfilesThatPropertyBelongsTo,

    addStagedOption,
    hideStagedOption,
    getStagedPropertyOptions,
    setStagedPropertyOptions,
    resetStagedPropertyOptions,
    getPropertyOptions,
    getPropertyOptionsAsync,
    getPropertyCulture,

    getPropertyComputeInstructions,
    getPropertyExportFormattingInstructions,
    getPropertyByKey,
  }
})
